import { ChangeDetectionStrategy, Component, OnInit, Signal, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideCircleAlert, lucideHouse } from '@ng-icons/lucide';

import { FocusOnlyRadioGroupDirective } from 'app/home/common/directives/focus-only-radio-group.directive';
import { ReadonlyRadioDirective } from 'app/home/common/directives/readonly-radio.directive';
import { BaseQuoteForm } from 'app/home/create-quote/form-config/quote-form-base';
import { QuoteFormContextDecorator } from 'app/home/create-quote/form-config/quote-form-decorator';
import { Lockable, WithLockableFields } from 'app/home/create-quote/form-locking';
import { LockedQuoteAlertComponent } from 'app/home/create-quote/form-locking/locked-quote-alert.component';
import { RoofShapeQuoteFormInput, RoofShapeValue } from 'app/home/create-quote/forms/property-details/roof-shape/roof-shape.types';
import { FormCardComponent } from 'app/home/create-quote/forms/shared/form-card.component';
import { QuoteDraftState } from 'app/home/global-store/quote-draft';
import { QuoteFormOptionsStore } from 'app/home/global-store/quote-form-options';
import { FeatureFlagSignalsService } from 'app/shared/services/splitIO/feature-flag.service';

@Component({
  selector: 'app-roof-shape',
  imports: [ReactiveFormsModule, FormCardComponent, NgIconComponent, ReadonlyRadioDirective, LockedQuoteAlertComponent, FocusOnlyRadioGroupDirective],
  providers: [provideIcons({ lucideHouse, lucideCircleAlert })],
  templateUrl: './roof-shape.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: contents;
      }
      // #kinetic-overrides
      .radio-card__title {
        margin-block-start: 0;
      }
      .radio-card__header {
        gap: var(--space-100);
      }
      .radio-card {
        align-items: flex-start; // headers should all be aligned even if their content spans different line lengths
      }
    `,
  ],
})
@QuoteFormContextDecorator({
  legacyShape: '/quote/property-details/roof-shape',
  isAutoAdvanced: true,
})
export class QuoteFormRoofShapeComponent extends BaseQuoteForm<RoofShapeQuoteFormInput> implements OnInit, Lockable {
  private featureFlagSignalsService = inject(FeatureFlagSignalsService);
  public isAutoAdvanceEnabled = this.featureFlagSignalsService.isAutoAdvanceEnabled;

  private optionsStore = inject(QuoteFormOptionsStore);
  public formDataIn: RoofShapeQuoteFormInput = this.quoteDraftStore.roofShapeQuoteFormSelector();

  public roofShapeOptions = this.optionsStore.roofShapeQuoteFormOptionsSelector();

  public roofShapeForm = new FormGroup({
    roofShape: new FormControl<RoofShapeValue | undefined>(undefined, Validators.required),
  });

  public override autoAdvanceControlRef = this.roofShapeForm.controls.roofShape;

  public ngOnInit() {
    if (this.formDataIn && this.isFieldLocked('roofShape')) {
      this.roofShapeForm.controls.roofShape.setValue(this.formDataIn.roofShape);
    }
  }

  private getFormValues(): Pick<QuoteDraftState, 'roofShape'> {
    const { controls } = this.roofShapeForm;
    const formValues = {
      roofShape: controls.roofShape.value ?? undefined,
    };

    return formValues;
  }

  public getFormControls() {
    return this.roofShapeForm.controls;
  }

  public isLocked: Signal<{ [key: string]: boolean }> = WithLockableFields.createIsLocked.call(this);
  public isFieldLocked = WithLockableFields.isFieldLocked;
  public allFieldsLocked = WithLockableFields.allFieldsLocked;

  public handleSubmit(e: Event) {
    e.preventDefault();
    this.roofShapeForm.markAllAsTouched();
    if (this.roofShapeForm.valid) {
      if (this.allFieldsLocked()) {
        super.navigateForward();
      } else {
        super.saveFormData(this.getFormValues());
      }
    }
  }

  get errorMessage(): string | null {
    if (this.isAutoAdvanceEnabled()) {
      return null;
    }
    const roofShapeControl = this.roofShapeForm.get('roofShape');

    if (!roofShapeControl) {
      return null;
    }

    const hasErrors = roofShapeControl.errors;
    const isTouchedOrDirty = roofShapeControl.touched || roofShapeControl.dirty;

    if (hasErrors && isTouchedOrDirty) {
      return 'This field is required';
    }

    return null;
  }
}
